import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { getWalletState, moveWalletFunds } from "@/utils/lending.functions";
import { inrPrecise } from "@/lib/lending-format";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Wallet } from "lucide-react";

export const Route = createFileRoute("/_authenticated/wallet")({
  head: () => ({ meta: [
    { title: "Wallet — LendyTrack" },
    { name: "description", content: "Manage deposits, withdrawals, and view your full transaction history." },
  ] }),
  component: WalletPage,
});

function WalletPage() {
  const q = useQuery({ queryKey: ["wallet"], queryFn: () => getWalletState() });
  const qc = useQueryClient();
  const move = useServerFn(moveWalletFunds);
  const [amount, setAmount] = useState("5000");

  const mut = useMutation({
    mutationFn: (kind: "deposit" | "withdraw") => move({ data: { kind, amount: Number(amount) } }),
    onSuccess: (_d, kind) => {
      toast.success(`${kind === "deposit" ? "Deposit" : "Withdrawal"} of ₹${amount} ${kind === "deposit" ? "added" : "processed"}.`);
      qc.invalidateQueries({ queryKey: ["wallet"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Wallet</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Manage your funds</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 rounded-xl border border-border bg-gradient-hero p-6 text-primary-foreground shadow-soft">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
            <Wallet className="h-3 w-3" /> Available balance
          </div>
          <div className="mt-2 font-display text-4xl font-semibold">{inrPrecise(q.data?.balance ?? 0)}</div>
          <div className="mt-1 text-xs opacity-80">Use for investments or withdraw to bank.</div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg font-semibold mb-3">Deposit or withdraw</h2>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">Amount (₹)</label>
              <input type="number" min={100} max={10000000} value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <button disabled={mut.isPending} onClick={() => mut.mutate("deposit")}
              className="rounded-md bg-success px-4 py-2 text-sm font-semibold text-success-foreground hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1">
              <ArrowDownToLine className="h-4 w-4" /> Deposit
            </button>
            <button disabled={mut.isPending} onClick={() => mut.mutate("withdraw")}
              className="rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1">
              <ArrowUpFromLine className="h-4 w-4" /> Withdraw
            </button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Deposits are simulated for the sandbox. Withdrawals are processed within 1 business day.</p>
        </div>
      </div>

      <h2 className="font-display text-xl font-semibold mt-10 mb-4">Transaction history</h2>
      {(q.data?.transactions ?? []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No transactions yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Notes</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.transactions ?? []).map((t) => {
                const isCredit = t.kind === "deposit" || t.kind === "repayment" || t.kind === "refund";
                return (
                  <tr key={t.id} className="border-t border-border">
                    <td className="px-4 py-3 text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 capitalize">{t.kind}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.notes ?? "—"}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${isCredit ? "text-success" : "text-foreground"}`}>
                      {isCredit ? "+" : "−"}{inrPrecise(t.amount)}
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{inrPrecise(t.balance_after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
